import { useCallback, useEffect, useState } from 'react';
import { MapRef } from 'react-map-gl';
import { AircraftAlert, OffScreenTrafficIndicator } from '../features/adsb/adsbTypes';
import { getDistanceBetweenPoints } from '../geometry';

// Inset (in pixels) from the true viewport edge so indicators don't hug the
// screen border and stay visible/tappable.
const EDGE_MARGIN_PX = 32;

const OFF_SCREEN_ALERT_LEVELS = new Set(['CRITICAL', 'WARNING']);

/**
 * Computes screen-edge arrow indicators for CRITICAL/WARNING ADS-B traffic
 * that has moved outside the current map viewport. Each indicator is clamped
 * to the viewport edge along the line from the screen center to the
 * aircraft, with an angle pointing toward the aircraft and the real-world
 * distance (km) from that edge point to the aircraft's actual position.
 *
 * Recomputes whenever the alert data changes and whenever the map is panned,
 * zoomed, or resized.
 */
const useOffScreenAdsbAlerts = (
  aircraftAlerts: AircraftAlert[],
  mapRef: MapRef | null,
  showTraffic: boolean = true
) => {
  const [indicators, setIndicators] = useState<OffScreenTrafficIndicator[]>([]);

  const recompute = useCallback(() => {
    const map = mapRef?.getMap();
    if (!map || !showTraffic) {
      setIndicators([]);
      return;
    }

    const container = map.getContainer();
    const width = container.clientWidth;
    const height = container.clientHeight;
    if (!width || !height) {
      setIndicators([]);
      return;
    }

    const centerX = width / 2;
    const centerY = height / 2;

    const next: OffScreenTrafficIndicator[] = [];

    aircraftAlerts
      .filter((aircraft) => OFF_SCREEN_ALERT_LEVELS.has(aircraft.alertLevel))
      .forEach((aircraft) => {
        const screenPoint = map.project([aircraft.lon, aircraft.lat]);
        const isOffScreen =
          screenPoint.x < 0 ||
          screenPoint.x > width ||
          screenPoint.y < 0 ||
          screenPoint.y > height;
        if (!isOffScreen) return;

        const dx = screenPoint.x - centerX;
        const dy = screenPoint.y - centerY;
        if (dx === 0 && dy === 0) return;

        // Find where the center->aircraft ray crosses the inset viewport
        // rectangle, so the arrow sits just inside the edge closest to the
        // aircraft's true direction.
        const tCandidates: number[] = [];
        if (dx > 0) tCandidates.push((width - EDGE_MARGIN_PX - centerX) / dx);
        if (dx < 0) tCandidates.push((EDGE_MARGIN_PX - centerX) / dx);
        if (dy > 0) tCandidates.push((height - EDGE_MARGIN_PX - centerY) / dy);
        if (dy < 0) tCandidates.push((EDGE_MARGIN_PX - centerY) / dy);
        if (tCandidates.length === 0) return;

        const t = Math.min(...tCandidates);
        const edgeX = centerX + t * dx;
        const edgeY = centerY + t * dy;

        // 0deg = up, matching the default orientation of an "arrow up" icon.
        const angleDeg = (Math.atan2(dx, -dy) * 180) / Math.PI;

        const edgeLngLat = map.unproject([edgeX, edgeY]);
        const distanceMeters = getDistanceBetweenPoints(
          [edgeLngLat.lat, edgeLngLat.lng],
          [aircraft.lat, aircraft.lon]
        );

        next.push({
          hex: aircraft.hex,
          alertLevel: aircraft.alertLevel,
          x: edgeX,
          y: edgeY,
          angleDeg,
          distanceKm: distanceMeters / 1000,
        });
      });

    setIndicators(next);
  }, [aircraftAlerts, mapRef, showTraffic]);

  useEffect(() => {
    recompute();
  }, [recompute]);

  // Keep indicators in sync while panning/zooming/resizing, not just when
  // alert data changes.
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return;

    map.on('move', recompute);
    map.on('resize', recompute);

    return () => {
      map.off('move', recompute);
      map.off('resize', recompute);
    };
  }, [mapRef, recompute]);

  return indicators;
};

export default useOffScreenAdsbAlerts;
